/**
 * Transition Banner Studio
 * — direct canvas editing (select · move · resize · rotate · snap)
 * — layers: fill, gradient (linear/radial), image (incl. circle), text, shape
 * — undo/redo, duplicate, lock, hide, drag to reorder
 * — timing (hold, transition length) and motion (zoom / pan)
 * — per-channel, account default and named templates
 *
 * Props are unchanged so Composition and the DreamFace panel keep working.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FaTrash, FaCopy, FaEye, FaEyeSlash, FaLock, FaLockOpen, FaUndo, FaRedo, FaPlay,
  FaFont, FaImage, FaSquare, FaCircle, FaFillDrip, FaPalette, FaUserCircle,
  FaAlignLeft, FaAlignCenter, FaAlignRight, FaArrowUp, FaArrowDown, FaExchangeAlt,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuthContext } from '../../hooks/UseAuthContext';
import { normalizeMediaUrl, resolveMediaUrl, uploadCompositionImage } from './uploadHelper';
import {
  BANNER_PRESETS,
  BANNER_FONTS,
  BANNER_MOTIONS,
  BANNER_TIMING,
  DEFAULT_BANNER_CONFIG,
  FFMPEG_TRANSITIONS,
  TRANSITION_GROUPS,
  CIRCLE_IMAGE_RADIUS,
  applyLayerBox,
  createCircleImageLayer,
  createLayer,
  getTransitionMeta,
  isCircleImageLayer,
  layerBox,
  layerLabel,
  newLayerId,
  normalizeBannerConfig,
  previewDimsForOrientation,
  primaryFontFamily,
  syncCircleLayerForCanvas,
} from './bannerPresets';
import {
  TEMPLATE_TYPES,
  buildBannerPayload,
  parseBannerPayload,
  cacheAccountBannerDefault,
  loadCachedAccountBannerDefault,
  cacheChannelBanner,
  loadCachedChannelBanner,
} from './compositionTemplates';
import { BannerScene, BannerThumb, layerImageSrc, useElementSize } from './bannerScene';
import {
  ColorField, Section, Segmented, SelectField, Slider, TemplateBar, Toggle, useHistory,
} from './studioKit';

/* Backward-compatible helpers other modules may still import */
export function loadAccountBannerDefault(user) { return loadCachedAccountBannerDefault(user); }
export function saveAccountBannerDefault(user, payload) { cacheAccountBannerDefault(user, payload); }
export function loadChannelBanner(user, channelId) { return loadCachedChannelBanner(user, channelId); }
export function saveChannelBanner(user, channelId, payload) { cacheChannelBanner(user, channelId, payload); }

const DEG = Math.PI / 180;
const rotate = (x, y, deg) => {
  const c = Math.cos(deg * DEG);
  const s = Math.sin(deg * DEG);
  return { x: x * c - y * s, y: x * s + y * c };
};
const isTypingTarget = (el) => {
  const tag = el?.tagName || '';
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el?.isContentEditable;
};

/* ═════════════════════════ Canvas ═════════════════════════ */
const HANDLES = [
  ['nw', -1, -1], ['n', 0, -1], ['ne', 1, -1], ['e', 1, 0],
  ['se', 1, 1], ['s', 0, 1], ['sw', -1, 1], ['w', -1, 0],
];
const SNAP_PX = 6;

function hitTest(layers, dims, x, y) {
  const sorted = [...layers].sort((a, b) => (b.zIndex ?? 0) - (a.zIndex ?? 0));
  for (const layer of sorted) {
    if (layer.visible === false) continue;
    const b = layerBox(layer, dims);
    const p = rotate(x - b.cx, y - b.cy, -b.rot);
    if (Math.abs(p.x) <= b.w / 2 && Math.abs(p.y) <= b.h / 2) return layer;
  }
  return null;
}

/** Axis-aligned bounds of a (possibly rotated) box. */
function aabb(b) {
  const c = Math.abs(Math.cos(b.rot * DEG));
  const s = Math.abs(Math.sin(b.rot * DEG));
  const hw = (b.w * c + b.h * s) / 2;
  const hh = (b.w * s + b.h * c) / 2;
  return { l: b.cx - hw, r: b.cx + hw, t: b.cy - hh, b: b.cy + hh, cx: b.cx, cy: b.cy };
}

function BannerCanvas({
  config,
  orientation,
  selectedId,
  onSelect,
  onLayerChange,
  onCheckpoint,
  showSafe,
  snapping,
  sceneRef,
  demoRef,
  playing,
}) {
  const exportDims = previewDimsForOrientation(orientation);
  const [areaRef, area] = useElementSize();
  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  const rafRef = useRef(0);
  const [guides, setGuides] = useState([]);
  const [hoverId, setHoverId] = useState(null);
  const [live, setLive] = useState(null); // in-progress layer during drag

  const ar = exportDims.w / exportDims.h;
  let w = Math.max(0, area.w - 32);
  let h = w / ar;
  if (h > area.h - 32) { h = Math.max(0, area.h - 32); w = h * ar; }
  const dims = { w, h };

  const layers = config.layers || [];
  const shownLayers = live ? layers.map((l) => (l.id === live.id ? live : l)) : layers;
  const shownConfig = live ? { ...config, layers: shownLayers } : config;
  const selected = shownLayers.find((l) => l.id === selectedId) || null;
  const hovered = hoverId && hoverId !== selectedId ? shownLayers.find((l) => l.id === hoverId) : null;

  const toCanvas = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const snapMove = (box, movingId) => {
    if (!snapping) return { box, guides: [] };
    const bb = aabb(box);
    const xs = [0, dims.w / 2, dims.w];
    const ys = [0, dims.h / 2, dims.h];
    shownLayers.forEach((l) => {
      if (l.id === movingId || l.visible === false) return;
      const o = aabb(layerBox(l, dims));
      xs.push(o.l, o.cx, o.r);
      ys.push(o.t, o.cy, o.b);
    });
    let dx = 0; let gx = null; let best = SNAP_PX + 1;
    [bb.l, bb.cx, bb.r].forEach((v) => xs.forEach((t) => {
      const d = t - v;
      if (Math.abs(d) < best) { best = Math.abs(d); dx = d; gx = t; }
    }));
    let dy = 0; let gy = null; best = SNAP_PX + 1;
    [bb.t, bb.cy, bb.b].forEach((v) => ys.forEach((t) => {
      const d = t - v;
      if (Math.abs(d) < best) { best = Math.abs(d); dy = d; gy = t; }
    }));
    const g = [];
    if (gx != null) g.push({ v: true, at: gx });
    if (gy != null) g.push({ v: false, at: gy });
    return { box: { ...box, cx: box.cx + (gx != null ? dx : 0), cy: box.cy + (gy != null ? dy : 0) }, guides: g };
  };

  const startDrag = (e, mode, extra = {}) => {
    const layer = extra.layer || selected;
    if (!layer || dims.w <= 0) return;
    if (layer.locked) return;
    e.preventDefault();
    e.stopPropagation();
    try { canvasRef.current.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    onCheckpoint();
    dragRef.current = {
      mode,
      id: layer.id,
      layer,
      start: toCanvas(e),
      box: layerBox(layer, dims),
      pointerId: e.pointerId,
      moved: false,
      ...extra,
    };
  };

  const onPointerDown = (e) => {
    if (playing || (e.pointerType === 'mouse' && e.button !== 0)) return;
    const role = e.target.dataset?.role;
    if (role === 'handle' && selected) { startDrag(e, 'resize', { handle: e.target.dataset.h }); return; }
    if (role === 'rotate' && selected) { startDrag(e, 'rotate'); return; }
    const p = toCanvas(e);
    // keep the current selection when clicking inside it (lets you move layers under others)
    if (selected) {
      const b = layerBox(selected, dims);
      const q = rotate(p.x - b.cx, p.y - b.cy, -b.rot);
      if (Math.abs(q.x) <= b.w / 2 && Math.abs(q.y) <= b.h / 2) { startDrag(e, 'move'); return; }
    }
    const hit = hitTest(layers, dims, p.x, p.y);
    onSelect(hit ? hit.id : null);
    if (hit) startDrag(e, 'move', { layer: hit });
  };

  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) {
      if (!playing && dims.w > 0 && e.pointerType === 'mouse') {
        const p = toCanvas(e);
        const hit = hitTest(layers, dims, p.x, p.y);
        setHoverId(hit ? hit.id : null);
      }
      return;
    }
    if (d.pointerId !== e.pointerId) return;
    const p = toCanvas(e);
    const dx = p.x - d.start.x;
    const dy = p.y - d.start.y;
    if (!d.moved && Math.hypot(dx, dy) < 2) return;
    d.moved = true;
    const b0 = d.box;
    let box = b0;
    let g = [];

    if (d.mode === 'move') {
      box = { ...b0, cx: b0.cx + dx, cy: b0.cy + dy };
      if (!e.altKey) ({ box, guides: g } = snapMove(box, d.id));
    } else if (d.mode === 'rotate') {
      let ang = Math.atan2(p.y - b0.cy, p.x - b0.cx) / DEG + 90;
      if (e.shiftKey) ang = Math.round(ang / 15) * 15;
      else {
        const near = Math.round(ang / 45) * 45;
        if (Math.abs(ang - near) < 4) ang = near;
      }
      ang = ((ang + 540) % 360) - 180;
      box = { ...b0, rot: ang };
    } else if (d.mode === 'resize') {
      const [, hx, hy] = HANDLES.find(([n]) => n === d.handle);
      const local = rotate(dx, dy, -b0.rot);
      const fromCenter = e.altKey;
      const k = fromCenter ? 2 : 1;
      let nw = hx ? b0.w + hx * local.x * k : b0.w;
      let nh = hy ? b0.h + hy * local.y * k : b0.h;
      const keepAspect = isCircleImageLayer(d.layer) || e.shiftKey || d.layer.lockAspect;
      if (keepAspect) {
        if (hx && hy) {
          const s = Math.max(nw / b0.w, nh / b0.h);
          nw = b0.w * s; nh = b0.h * s;
        } else if (hx) { nh = b0.h * (nw / b0.w); } else { nw = b0.w * (nh / b0.h); }
      }
      nw = Math.max(8, nw);
      nh = Math.max(8, nh);
      let cx = b0.cx;
      let cy = b0.cy;
      if (!fromCenter) {
        const shift = rotate(hx * (nw - b0.w) / 2, hy * (nh - b0.h) / 2, b0.rot);
        cx += shift.x; cy += shift.y;
      }
      box = { ...b0, cx, cy, w: nw, h: nh };
    }

    const next = applyLayerBox(d.layer, box, dims);
    d.last = next;
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      setLive(next);
      setGuides(g);
    });
  };

  const endDrag = (e) => {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    dragRef.current = null;
    cancelAnimationFrame(rafRef.current);
    try { canvasRef.current.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    if (d.moved && d.last) onLayerChange(d.last);
    setLive(null);
    setGuides([]);
  };

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  const selBox = selected && !playing ? layerBox(selected, dims) : null;
  const hovBox = hovered && !playing ? layerBox(hovered, dims) : null;
  const boxStyle = (b) => ({
    left: b.cx - b.w / 2,
    top: b.cy - b.h / 2,
    width: b.w,
    height: b.h,
    transform: b.rot ? `rotate(${b.rot}deg)` : undefined,
  });

  return (
    <div ref={areaRef} className="st-stage__area">
      {dims.w > 0 && (
        <div
          ref={canvasRef}
          className="st-canvas"
          style={{ width: dims.w, height: dims.h }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onPointerLeave={() => setHoverId(null)}
          onDoubleClick={() => { if (selected?.type === 'text') document.getElementById('banner-text-input')?.focus(); }}
        >
          <BannerScene config={shownConfig} dims={dims} exportDims={exportDims} innerRef={sceneRef} />
          <div ref={demoRef} className="st-demo" aria-hidden="true" />
          {showSafe && !playing && <div className="st-safe" />}
          {guides.map((g, i) => (
            <div
              key={i}
              className={`st-guide ${g.v ? 'st-guide--v' : 'st-guide--h'}`}
              style={g.v ? { left: g.at } : { top: g.at }}
            />
          ))}
          {hovBox && <div className="st-hover" style={boxStyle(hovBox)} />}
          {selBox && (
            <div
              className={[
                'st-sel',
                selected.locked ? 'is-locked' : '',
                selBox.h < 48 ? 'is-thin-h' : '',
                selBox.w < 48 ? 'is-thin-w' : '',
              ].filter(Boolean).join(' ')}
              style={boxStyle(selBox)}
            >
              <span className="st-sel__tag">
                {selected.locked ? '🔒 ' : ''}
                {Math.round(selBox.w * (exportDims.w / dims.w))}×{Math.round(selBox.h * (exportDims.h / dims.h))}
                {selBox.rot ? ` · ${Math.round(selBox.rot)}°` : ''}
              </span>
              {!selected.locked && (
                <>
                  {HANDLES
                    .filter(([n]) => !isCircleImageLayer(selected) || n.length === 2)
                    .map(([n]) => <span key={n} className={`st-sel__h st-sel__h--${n}`} data-role="handle" data-h={n} />)}
                  <span className="st-sel__rot" data-role="rotate" title="Rotate (Shift = 15° steps)" />
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ═════════════════════ Transition demo ═════════════════════ */
function transitionFrames(type) {
  switch (type) {
    case 'swipe_left': return { out: [{ transform: 'translateX(0)' }, { transform: 'translateX(-100%)' }], in: [{ transform: 'translateX(100%)' }, { transform: 'translateX(0)' }] };
    case 'swipe_right': return { out: [{ transform: 'translateX(0)' }, { transform: 'translateX(100%)' }], in: [{ transform: 'translateX(-100%)' }, { transform: 'translateX(0)' }] };
    case 'swipe_up': return { out: [{ transform: 'translateY(0)' }, { transform: 'translateY(-100%)' }], in: [{ transform: 'translateY(100%)' }, { transform: 'translateY(0)' }] };
    case 'swipe_down': return { out: [{ transform: 'translateY(0)' }, { transform: 'translateY(100%)' }], in: [{ transform: 'translateY(-100%)' }, { transform: 'translateY(0)' }] };
    case 'wipe_left':
    case 'smooth_left': return { in: [{ clipPath: 'inset(0 0 0 100%)' }, { clipPath: 'inset(0 0 0 0)' }], inTop: true };
    case 'wipe_right': return { in: [{ clipPath: 'inset(0 100% 0 0)' }, { clipPath: 'inset(0 0 0 0)' }], inTop: true };
    case 'circle_open': return { in: [{ clipPath: 'circle(0% at 50% 50%)' }, { clipPath: 'circle(75% at 50% 50%)' }], inTop: true };
    case 'circle_close': return { out: [{ clipPath: 'circle(75% at 50% 50%)' }, { clipPath: 'circle(0% at 50% 50%)' }], outTop: true };
    case 'zoom_blur': return { in: [{ opacity: 0, transform: 'scale(1.18)', filter: 'blur(8px)' }, { opacity: 1, transform: 'scale(1)', filter: 'blur(0)' }], inTop: true };
    case 'fade_black': return { bridge: '#000' };
    case 'fade_white': return { bridge: '#fff' };
    default: return { in: [{ opacity: 0 }, { opacity: 1 }], inTop: true };
  }
}

function motionFrames(motion, amount) {
  const a = 1 + amount;
  const p = `${(amount / 2) * 100}%`;
  switch (motion) {
    case 'zoom_in': return [{ transform: 'scale(1)' }, { transform: `scale(${a})` }];
    case 'zoom_out': return [{ transform: `scale(${a})` }, { transform: 'scale(1)' }];
    case 'pan_left': return [{ transform: `scale(${a}) translateX(${p})` }, { transform: `scale(${a}) translateX(-${p})` }];
    case 'pan_right': return [{ transform: `scale(${a}) translateX(-${p})` }, { transform: `scale(${a}) translateX(${p})` }];
    default: return null;
  }
}

/* ═════════════════════ Inspector pieces ═════════════════════ */
function ImageUploadField({ value, onChange, token }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const preview = resolveMediaUrl(value);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image file'); return; }
    setUploading(true);
    try {
      const url = await uploadCompositionImage(file, token);
      if (!url) throw new Error('no url');
      onChange(url);
      toast.success('Image uploaded');
    } catch {
      toast.error('Upload failed — paste the image URL instead');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div
      className="st-field st-upload"
      onDragOver={(e) => { e.preventDefault(); }}
      onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
    >
      <span className="st-field__label">Image</span>
      <button
        type="button"
        className={`st-upload__drop ${preview ? 'has-image' : ''}`}
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
      >
        {preview ? <img src={preview} alt="" draggable={false} /> : null}
        <span>{uploading ? 'Uploading…' : preview ? 'Replace image' : 'Click or drop an image'}</span>
      </button>
      <input
        className="st-input"
        type="text"
        placeholder="…or paste an image URL"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => { const n = normalizeMediaUrl(value); if (n && n !== value) onChange(n); }}
      />
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={(e) => handleFile(e.target.files?.[0])} />
    </div>
  );
}

function LayerInspector({ layer, exportDims, onChange, onDuplicate, onDelete, onArrange, userToken }) {
  const set = (patch) => onChange({ ...layer, ...patch });
  const box = layerBox(layer, exportDims);
  const setBox = (patch) => onChange(applyLayerBox(layer, { ...box, ...patch }, exportDims));
  const circle = isCircleImageLayer(layer);

  const alignTo = (where) => {
    const bb = aabb(box);
    const hw = (bb.r - bb.l) / 2;
    const hh = (bb.b - bb.t) / 2;
    const map = {
      left: { cx: hw }, hcenter: { cx: exportDims.w / 2 }, right: { cx: exportDims.w - hw },
      top: { cy: hh }, vcenter: { cy: exportDims.h / 2 }, bottom: { cy: exportDims.h - hh },
    };
    setBox(map[where]);
  };

  return (
    <>
      <Section
        title="Layer"
        right={(
          <span style={{ display: 'flex', gap: 2 }}>
            <button type="button" className="st-icon-btn" onClick={() => onArrange('up')} title="Bring forward" aria-label="Bring forward"><FaArrowUp /></button>
            <button type="button" className="st-icon-btn" onClick={() => onArrange('down')} title="Send backward" aria-label="Send backward"><FaArrowDown /></button>
            <button type="button" className="st-icon-btn" onClick={onDuplicate} title="Duplicate (Ctrl+D)"><FaCopy /></button>
            <button type="button" className="st-icon-btn is-danger" onClick={onDelete} title="Delete (Del)"><FaTrash /></button>
          </span>
        )}
      >
        <div className="st-field">
          <label className="st-field__label" htmlFor="banner-layer-name">Name</label>
          <input
            id="banner-layer-name"
            className="st-input"
            value={layer.name || ''}
            placeholder={layerLabel({ ...layer, name: '' })}
            onChange={(e) => set({ name: e.target.value })}
          />
        </div>
        <div className="st-toggles">
          <Toggle label="Visible" checked={layer.visible !== false} onChange={(v) => set({ visible: v })} />
          <Toggle label="Locked" checked={!!layer.locked} onChange={(v) => set({ locked: v })} />
          {!circle && layer.type !== 'fill' && layer.type !== 'gradient' && (
            <Toggle label="Keep ratio" checked={!!layer.lockAspect} onChange={(v) => set({ lockAspect: v })} />
          )}
        </div>
        <Slider label="Opacity" value={layer.opacity ?? 1} min={0} max={1} step={0.01} scale={100} unit="%" decimals={0} onChange={(v) => set({ opacity: v })} />
      </Section>

      <Section title="Position & size">
        <div className="st-align">
          {[
            ['left', 'Align left'], ['hcenter', 'Centre horizontally'], ['right', 'Align right'],
            ['top', 'Align top'], ['vcenter', 'Centre vertically'], ['bottom', 'Align bottom'],
          ].map(([k, t]) => (
            <button key={k} type="button" className={`st-icon-btn st-align__${k}`} onClick={() => alignTo(k)} title={t} aria-label={t}>
              <span className="st-align__glyph" />
            </button>
          ))}
        </div>
        <div className="st-grid2">
          <Slider label="X (centre)" value={(box.cx / exportDims.w) * 100} min={-20} max={120} step={0.5} unit="%" decimals={1} onChange={(v) => setBox({ cx: (v / 100) * exportDims.w })} />
          <Slider label="Y (centre)" value={(box.cy / exportDims.h) * 100} min={-20} max={120} step={0.5} unit="%" decimals={1} onChange={(v) => setBox({ cy: (v / 100) * exportDims.h })} />
          {circle ? (
            <Slider label="Diameter" value={layer.sizePct ?? 28} min={4} max={100} step={0.5} unit="%" decimals={1} onChange={(v) => onChange(syncCircleLayerForCanvas({ ...layer, sizePct: v }, exportDims))} />
          ) : (
            <>
              <Slider label="Width" value={layer.widthPct ?? 100} min={1} max={150} step={0.5} unit="%" decimals={1} onChange={(v) => setBox({ w: (v / 100) * exportDims.w })} />
              <Slider label="Height" value={layer.heightPct ?? 100} min={1} max={150} step={0.5} unit="%" decimals={1} onChange={(v) => setBox({ h: (v / 100) * exportDims.h })} />
            </>
          )}
        </div>
        <Slider label="Rotation" value={layer.rotation || 0} min={-180} max={180} step={1} unit="°" onChange={(v) => setBox({ rot: v })} />
      </Section>

      {layer.type === 'fill' && (
        <Section title="Fill">
          <ColorField label="Colour" value={layer.color} onChange={(v) => set({ color: v })} />
        </Section>
      )}

      {layer.type === 'gradient' && (
        <Section title="Gradient">
          <Segmented
            options={[{ value: 'linear', label: 'Linear' }, { value: 'radial', label: 'Radial' }]}
            value={layer.gradientType || 'linear'}
            onChange={(v) => set({ gradientType: v })}
          />
          <div className="st-grid2">
            <ColorField label="From" value={layer.gradientFrom} onChange={(v) => set({ gradientFrom: v })} />
            <ColorField label="To" value={layer.gradientTo} onChange={(v) => set({ gradientTo: v })} />
          </div>
          <button type="button" className="st-btn st-btn--sm" onClick={() => set({ gradientFrom: layer.gradientTo, gradientTo: layer.gradientFrom })}>
            <FaExchangeAlt /> Swap colours
          </button>
          {(layer.gradientType || 'linear') === 'linear' && (
            <Slider label="Angle" value={layer.gradientAngle ?? 135} min={0} max={360} step={1} unit="°" onChange={(v) => set({ gradientAngle: v })} />
          )}
        </Section>
      )}

      {layer.type === 'shape' && (
        <Section title="Shape">
          <Segmented
            options={[{ value: 'rect', label: 'Rectangle' }, { value: 'ellipse', label: 'Ellipse' }]}
            value={layer.shapeKind || 'rect'}
            onChange={(v) => set({ shapeKind: v })}
          />
          <Toggle label="Fill" checked={layer.fillEnabled !== false} onChange={(v) => set({ fillEnabled: v })} />
          {layer.fillEnabled !== false && <ColorField label="Fill colour" value={layer.color} onChange={(v) => set({ color: v })} />}
          <Toggle label="Outline" checked={!!layer.strokeEnabled} onChange={(v) => set({ strokeEnabled: v })} />
          {layer.strokeEnabled && (
            <div className="st-grid2">
              <ColorField label="Outline colour" value={layer.strokeColor} onChange={(v) => set({ strokeColor: v })} />
              <Slider label="Outline" value={layer.strokeWidthPct ?? 0.004} min={0.001} max={0.03} step={0.001} scale={1000} decimals={0} onChange={(v) => set({ strokeWidthPct: v })} />
            </div>
          )}
          {(layer.shapeKind || 'rect') === 'rect' && (
            <Slider label="Corner radius" value={layer.borderRadiusPct ?? 0} min={0} max={0.5} step={0.01} scale={100} unit="%" decimals={0} onChange={(v) => set({ borderRadiusPct: v })} />
          )}
        </Section>
      )}

      {layer.type === 'image' && (
        <Section title="Image">
          <ImageUploadField value={layer.imageUrl} onChange={(url) => set({ imageUrl: url })} token={userToken} />
          <Segmented
            label="Fit"
            options={[{ value: 'cover', label: 'Fill' }, { value: 'contain', label: 'Fit' }]}
            value={layer.objectFit === 'contain' ? 'contain' : 'cover'}
            onChange={(v) => set({ objectFit: v })}
          />
          <Segmented
            label="Shape"
            options={[{ value: 'rect', label: 'Rectangle' }, { value: 'circle', label: 'Circle' }]}
            value={circle ? 'circle' : 'rect'}
            onChange={(v) => {
              if (v === 'circle') {
                onChange(syncCircleLayerForCanvas({
                  ...layer, anchor: 'center', xPct: (box.cx / exportDims.w) * 100, yPct: (box.cy / exportDims.h) * 100,
                  shape: 'circle', sizePct: (Math.min(box.w, box.h) / Math.min(exportDims.w, exportDims.h)) * 100, borderRadiusPct: CIRCLE_IMAGE_RADIUS,
                }, exportDims));
              } else {
                const rest = { ...layer };
                delete rest.shape;
                delete rest.sizePct;
                onChange({ ...rest, borderRadiusPct: 0.08 });
              }
            }}
          />
          {!circle && (
            <Slider label="Corner radius" value={layer.borderRadiusPct ?? 0} min={0} max={0.5} step={0.01} scale={100} unit="%" decimals={0} onChange={(v) => set({ borderRadiusPct: v })} />
          )}
          <div className="st-grid2">
            <Slider label="Border" value={layer.borderWidth ?? 0} min={0} max={24} step={1} unit="px" onChange={(v) => set({ borderWidth: v })} />
            <ColorField label="Border colour" value={layer.borderColor} onChange={(v) => set({ borderColor: v })} />
          </div>
          {layer.objectFit !== 'contain' && (
            <div className="st-grid2">
              <Slider label="Focus X" value={layer.imageFocusXPct ?? 50} min={0} max={100} step={1} unit="%" onChange={(v) => set({ imageFocusXPct: v })} />
              <Slider label="Focus Y" value={layer.imageFocusYPct ?? 50} min={0} max={100} step={1} unit="%" onChange={(v) => set({ imageFocusYPct: v })} />
            </div>
          )}
        </Section>
      )}

      {layer.type === 'text' && (
        <>
          <Section title="Text">
            <textarea
              id="banner-text-input"
              className="st-textarea"
              rows={3}
              value={layer.text || ''}
              onChange={(e) => set({ text: e.target.value })}
              placeholder="Type your text — Enter for a new line"
            />
            <SelectField
              label="Font"
              value={primaryFontFamily(layer.fontFamily)}
              onChange={(v) => set({ fontFamily: v })}
              options={BANNER_FONTS.map((f) => ({ value: f.value, label: f.label }))}
            />
            <Segmented
              label="Weight"
              options={[{ value: '400', label: 'Regular' }, { value: '700', label: 'Bold' }, { value: '900', label: 'Heavy' }]}
              value={Number(layer.fontWeight) >= 800 ? '900' : Number(layer.fontWeight) >= 600 || layer.fontWeight === 'bold' ? '700' : '400'}
              onChange={(v) => set({ fontWeight: v })}
            />
            <div className="st-grid2">
              <Slider label="Size" value={layer.fontSizePct ?? 0.065} min={0.015} max={0.25} step={0.001} scale={100} unit="%" decimals={1} onChange={(v) => set({ fontSizePct: v })} />
              <ColorField label="Colour" value={layer.color} onChange={(v) => set({ color: v })} />
              <Slider label="Line height" value={layer.lineHeight ?? 1.15} min={0.7} max={2} step={0.05} decimals={2} onChange={(v) => set({ lineHeight: v })} />
              <Slider label="Spacing" value={layer.letterSpacing ?? 0} min={-0.5} max={4} step={0.05} decimals={2} onChange={(v) => set({ letterSpacing: v })} />
            </div>
            <div className="st-grid2">
              <Segmented
                label="Align"
                options={[
                  { value: 'left', label: 'Left', icon: <FaAlignLeft /> },
                  { value: 'center', label: 'Centre', icon: <FaAlignCenter /> },
                  { value: 'right', label: 'Right', icon: <FaAlignRight /> },
                ]}
                value={layer.textAlign || 'center'}
                onChange={(v) => set({ textAlign: v })}
              />
              <Segmented
                label="Case"
                options={[{ value: 'none', label: 'Aa' }, { value: 'uppercase', label: 'AA' }, { value: 'lowercase', label: 'aa' }]}
                value={layer.textTransform || 'none'}
                onChange={(v) => set({ textTransform: v })}
              />
            </div>
          </Section>
          <Section title="Text effects" defaultOpen={false}>
            <Toggle label="Shadow" checked={layer.shadowEnabled !== false} onChange={(v) => set({ shadowEnabled: v })} />
            {layer.shadowEnabled !== false && (
              <div className="st-grid2">
                <ColorField label="Shadow colour" value={layer.shadowColor || '#000000'} onChange={(v) => set({ shadowColor: v })} />
                <Slider label="Strength" value={layer.shadowOpacity ?? 0.45} min={0} max={1} step={0.05} scale={100} unit="%" decimals={0} onChange={(v) => set({ shadowOpacity: v })} />
                <Slider label="Blur" value={layer.shadowBlurPct ?? 0.011} min={0} max={0.05} step={0.001} scale={1000} decimals={0} onChange={(v) => set({ shadowBlurPct: v })} />
                <Slider label="Distance" value={layer.shadowOffsetYPct ?? 0.004} min={-0.03} max={0.03} step={0.001} scale={1000} decimals={0} onChange={(v) => set({ shadowOffsetYPct: v })} />
              </div>
            )}
            <Toggle label="Outline" checked={(layer.strokeWidthPct || 0) > 0} onChange={(v) => set({ strokeWidthPct: v ? 0.004 : 0 })} />
            {(layer.strokeWidthPct || 0) > 0 && (
              <div className="st-grid2">
                <ColorField label="Outline colour" value={layer.strokeColor || '#000000'} onChange={(v) => set({ strokeColor: v })} />
                <Slider label="Thickness" value={layer.strokeWidthPct} min={0.001} max={0.02} step={0.0005} scale={1000} decimals={1} onChange={(v) => set({ strokeWidthPct: v })} />
              </div>
            )}
            <Toggle label="Background box" checked={!!layer.bgEnabled} onChange={(v) => set({ bgEnabled: v })} />
            {layer.bgEnabled && (
              <div className="st-grid2">
                <ColorField label="Box colour" value={layer.bgColor || '#000000'} onChange={(v) => set({ bgColor: v })} />
                <Slider label="Box opacity" value={layer.bgOpacity ?? 0.6} min={0} max={1} step={0.05} scale={100} unit="%" decimals={0} onChange={(v) => set({ bgOpacity: v })} />
                <Slider label="Box radius" value={layer.bgRadiusPct ?? 0.2} min={0} max={0.5} step={0.01} scale={100} unit="%" decimals={0} onChange={(v) => set({ bgRadiusPct: v })} />
                <Slider label="Padding" value={layer.paddingPct ?? 0.004} min={0} max={0.06} step={0.001} scale={1000} decimals={0} onChange={(v) => set({ paddingPct: v })} />
              </div>
            )}
          </Section>
        </>
      )}
      <p className="st-hint" style={{ padding: '10px 12px 16px' }}>
        Drag on the canvas to move · handles resize (<span className="st-kbd">Shift</span> keeps ratio,
        {' '}<span className="st-kbd">Alt</span> from centre) · top knob rotates · arrows nudge.
      </p>
    </>
  );
}

function BannerSettings({ cfg, setConfig, onReplay }) {
  const meta = getTransitionMeta(cfg.transitionType);
  return (
    <>
      <Section title="Transition">
        <SelectField
          label="Style"
          value={cfg.transitionType}
          onChange={(v) => { setConfig({ transitionType: v }, { commit: true }); onReplay(); }}
          options={TRANSITION_GROUPS.map((g) => ({
            group: g.group,
            options: g.ids.map((id) => ({ value: id, label: getTransitionMeta(id).label })),
          }))}
        />
        <p className="st-hint">{meta?.description}</p>
        <button type="button" className="st-btn st-btn--sm" onClick={onReplay}><FaPlay /> Preview banner</button>
      </Section>
      <Section title="Timing">
        <Slider label="On screen (hold)" value={cfg.holdSec} min={BANNER_TIMING.holdSec.min} max={BANNER_TIMING.holdSec.max} step={0.05} unit="s" decimals={2} onChange={(v) => setConfig({ holdSec: v })} />
        <Slider label="Transition length" value={cfg.transitionSec} min={BANNER_TIMING.transitionSec.min} max={BANNER_TIMING.transitionSec.max} step={0.05} unit="s" decimals={2} onChange={(v) => setConfig({ transitionSec: v })} />
        <p className="st-hint">
          Each banner adds about {(cfg.holdSec).toFixed(1)}s to the video. The transition length is capped by how long the
          neighbouring clips are.
        </p>
      </Section>
      <Section title="Motion">
        <Segmented
          options={BANNER_MOTIONS.map((m) => ({ value: m.value, label: m.label }))}
          value={cfg.motion || 'none'}
          onChange={(v) => { setConfig({ motion: v }, { commit: true }); onReplay(); }}
        />
        {cfg.motion && cfg.motion !== 'none' && (
          <Slider label="Amount" value={cfg.motionAmount ?? 0.08} min={0.02} max={0.3} step={0.01} scale={100} unit="%" decimals={0} onChange={(v) => setConfig({ motionAmount: v })} />
        )}
      </Section>
      <p className="st-hint" style={{ padding: '10px 12px 16px' }}>
        Select a layer on the canvas or in the list to edit it. <span className="st-kbd">Ctrl</span>+<span className="st-kbd">Z</span> undo,
        {' '}<span className="st-kbd">Ctrl</span>+<span className="st-kbd">D</span> duplicate, <span className="st-kbd">Del</span> delete.
      </p>
    </>
  );
}

/* ═════════════════════════ Studio ═════════════════════════ */
const ADD_ITEMS = [
  { kind: 'text', label: 'Text', icon: <FaFont /> },
  { kind: 'image', label: 'Image', icon: <FaImage /> },
  { kind: 'circle_image', label: 'Circle', icon: <FaUserCircle /> },
  { kind: 'shape', label: 'Box', icon: <FaSquare /> },
  { kind: 'ellipse', label: 'Ellipse', icon: <FaCircle /> },
  { kind: 'fill', label: 'Fill', icon: <FaFillDrip /> },
  { kind: 'gradient', label: 'Gradient', icon: <FaPalette /> },
];

function layerIcon(layer) {
  if (layer.type === 'image') {
    const src = layerImageSrc(layer);
    return src ? <img src={src} alt="" draggable={false} /> : <FaImage />;
  }
  if (layer.type === 'text') return <FaFont />;
  if (layer.type === 'shape') return layer.shapeKind === 'ellipse' ? <FaCircle style={{ color: layer.color }} /> : <FaSquare style={{ color: layer.color }} />;
  if (layer.type === 'fill') return <span style={{ width: 14, height: 14, borderRadius: 3, background: layer.color }} />;
  if (layer.type === 'gradient') return <span style={{ width: 14, height: 14, borderRadius: 3, background: `linear-gradient(135deg, ${layer.gradientFrom}, ${layer.gradientTo})` }} />;
  return null;
}

export default function TransitionBannerPanel({
  bannerEnabled,
  setBannerEnabled,
  bannerConfig,
  setBannerConfig,
  presetId,
  setPresetId,
  orientation,
  user: userProp,
  userToken: tokenProp,
}) {
  const { user: ctxUser } = useAuthContext() || {};
  const user = userProp || ctxUser;
  const userToken = tokenProp || user?.token;
  const orient = orientation === 'portrait' ? 'portrait' : 'landscape';
  const exportDims = previewDimsForOrientation(orient);

  const cfg = useMemo(() => normalizeBannerConfig(bannerConfig), [bannerConfig]);
  const history = useHistory(cfg, setBannerConfig);
  const [selectedId, setSelectedId] = useState(null);
  const [railTab, setRailTab] = useState('layers');
  const [mobileTab, setMobileTab] = useState('inspect');
  const [showSafe, setShowSafe] = useState(false);
  const [snapping, setSnapping] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [dragLayerId, setDragLayerId] = useState(null);
  const [dropLayerId, setDropLayerId] = useState(null);
  const [playRequest, setPlayRequest] = useState(0);
  const sceneRef = useRef(null);
  const demoRef = useRef(null);
  const animsRef = useRef([]);
  const rootRef = useRef(null);

  const layers = useMemo(() => cfg.layers || [], [cfg.layers]);
  const selected = layers.find((l) => l.id === selectedId) || null;

  const markCustom = useCallback(() => { if (presetId !== 'custom') setPresetId('custom'); }, [presetId, setPresetId]);

  /** Patch the banner config (object merge or updater). */
  const setConfig = useCallback((patch, opts) => {
    history.set((prev) => {
      const base = normalizeBannerConfig(prev);
      return typeof patch === 'function' ? patch(base) : { ...base, ...patch };
    }, opts);
    markCustom();
  }, [history, markCustom]);

  const updateLayer = useCallback((updated, opts) => {
    setConfig((prev) => ({ ...prev, layers: prev.layers.map((l) => (l.id === updated.id ? { ...l, ...updated } : l)) }), opts);
  }, [setConfig]);

  const addLayer = (kind) => {
    const maxZ = layers.reduce((m, l) => Math.max(m, l.zIndex ?? 0), -1);
    let layer;
    if (kind === 'circle_image') layer = syncCircleLayerForCanvas(createCircleImageLayer({ zIndex: maxZ + 1, sizePct: 30, xPct: 50, yPct: 50 }), exportDims);
    else if (kind === 'ellipse') layer = createLayer('shape', { zIndex: maxZ + 1, shapeKind: 'ellipse', widthPct: 20, heightPct: 20 * (exportDims.w / exportDims.h) });
    else if (kind === 'fill' || kind === 'gradient') layer = createLayer(kind, { zIndex: maxZ + 1, opacity: kind === 'fill' ? 0.5 : 1 });
    else layer = createLayer(kind, { zIndex: maxZ + 1 });
    setConfig((prev) => ({ ...prev, layers: [...prev.layers, layer] }), { commit: true });
    setSelectedId(layer.id);
    setMobileTab('inspect');
  };

  const removeLayer = useCallback((id) => {
    setConfig((prev) => ({ ...prev, layers: prev.layers.filter((l) => l.id !== id) }), { commit: true });
    setSelectedId((s) => (s === id ? null : s));
  }, [setConfig]);

  const duplicateLayer = useCallback((id) => {
    const src = layers.find((l) => l.id === id);
    if (!src) return;
    const box = layerBox(src, exportDims);
    const copy = applyLayerBox({ ...src, id: newLayerId(src.type), name: src.name ? `${src.name} copy` : '' }, { ...box, cx: box.cx + exportDims.w * 0.02, cy: box.cy + exportDims.h * 0.02 }, exportDims);
    copy.zIndex = (src.zIndex ?? 0) + 0.5;
    setConfig((prev) => {
      const list = [...prev.layers, copy].sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0));
      return { ...prev, layers: list.map((l, i) => ({ ...l, zIndex: i })) };
    }, { commit: true });
    setSelectedId(copy.id);
  }, [layers, exportDims, setConfig]);

  const reorder = (fromId, toId) => {
    if (!fromId || !toId || fromId === toId) return;
    setConfig((prev) => {
      const topFirst = [...prev.layers].sort((a, b) => (b.zIndex ?? 0) - (a.zIndex ?? 0));
      const from = topFirst.findIndex((l) => l.id === fromId);
      const to = topFirst.findIndex((l) => l.id === toId);
      const [item] = topFirst.splice(from, 1);
      topFirst.splice(to, 0, item);
      const n = topFirst.length;
      return { ...prev, layers: topFirst.map((l, i) => ({ ...l, zIndex: n - 1 - i })) };
    }, { commit: true });
  };

  const moveStep = (id, dir) => {
    const topFirst = [...layers].sort((a, b) => (b.zIndex ?? 0) - (a.zIndex ?? 0));
    const i = topFirst.findIndex((l) => l.id === id);
    const j = dir === 'up' ? i - 1 : i + 1;
    if (j < 0 || j >= topFirst.length) return;
    reorder(id, topFirst[j].id);
  };

  const applyPreset = (preset) => {
    history.set(normalizeBannerConfig(JSON.parse(JSON.stringify(preset.config))), { commit: true });
    setPresetId(preset.id);
    setSelectedId(null);
    setPlayRequest((n) => n + 1); // play once the new design has rendered
  };

  const applySaved = useCallback((saved) => {
    if (!saved?.config) return;
    history.set(normalizeBannerConfig(saved.config), { commit: true });
    setPresetId(saved.presetId || 'custom');
    if (saved.enabled != null) setBannerEnabled(saved.enabled !== false);
    setSelectedId(null);
  }, [history, setPresetId, setBannerEnabled]);

  /* ── Preview playback (transition in → hold + motion → transition out) ── */
  const playTimerRef = useRef(0);
  const stop = useCallback(() => {
    window.clearTimeout(playTimerRef.current);
    animsRef.current.forEach((a) => { try { a.cancel(); } catch { /* ignore */ } });
    animsRef.current = [];
    if (demoRef.current) demoRef.current.innerHTML = '';
    setPlaying(false);
  }, []);

  const play = useCallback(() => {
    const scene = sceneRef.current;
    const demo = demoRef.current;
    if (!scene || !demo || typeof scene.animate !== 'function') return;
    stop();
    setPlaying(true);
    const T = cfg.transitionSec * 1000;
    const Hd = cfg.holdSec * 1000;
    const total = T * 2 + Hd;
    const fr = transitionFrames(cfg.transitionType);
    const mk = (cls) => { const d = document.createElement('div'); d.className = cls; return d; };
    const from = mk('st-demo__from');
    const to = mk('st-demo__to');
    const bridge = mk('st-demo__from');
    bridge.style.background = fr.bridge || '#000';
    bridge.style.opacity = '0';
    to.style.opacity = '0';
    demo.append(from, to, bridge);

    const ease = 'cubic-bezier(0.45,0,0.2,1)';
    const hasTransform = (frames) => (frames || []).some((k) => k.transform);
    const A = [];
    const run = (el, frames, delay, duration, opts = {}) => {
      if (!frames) return;
      A.push(el.animate(frames, { delay, duration, easing: ease, fill: delay ? 'forwards' : 'both', ...opts }));
    };
    const onTop = (el, delay, duration) => {
      A.push(el.animate([{ zIndex: 1300 }, { zIndex: 1300 }], { delay, duration }));
    };

    // camera motion across the whole banner
    const mf = motionFrames(cfg.motion, cfg.motionAmount ?? 0.08);
    if (mf) A.push(scene.animate(mf, { duration: total, fill: 'both', easing: 'linear' }));
    const sceneOpts = (frames) => (hasTransform(frames) && mf ? { composite: 'add' } : {});

    // A: previous scene → banner
    if (fr.bridge) {
      run(bridge, [{ opacity: 0 }, { opacity: 1 }, { opacity: 0 }], 0, T);
      run(from, [{ opacity: 1 }, { opacity: 1, offset: 0.5 }, { opacity: 0, offset: 0.5 }, { opacity: 0 }], 0, T);
    } else if (fr.out && fr.in) {
      run(from, fr.out, 0, T);
      run(scene, fr.in, 0, T, sceneOpts(fr.in));
    } else if (fr.inTop) {
      onTop(scene, 0, T);
      run(scene, fr.in, 0, T, sceneOpts(fr.in));
    } else if (fr.outTop) {
      run(from, fr.out, 0, T);
    }
    A.push(from.animate([{ visibility: 'visible' }, { visibility: 'hidden' }], { delay: T, duration: 1, fill: 'forwards' }));

    // C: banner → next scene
    const c0 = T + Hd;
    if (fr.bridge) {
      run(bridge, [{ opacity: 0 }, { opacity: 1 }, { opacity: 0 }], c0, T);
      run(to, [{ opacity: 0 }, { opacity: 0, offset: 0.5 }, { opacity: 1, offset: 0.5 }, { opacity: 1 }], c0, T);
    } else if (fr.out && fr.in) {
      run(to, [{ opacity: 1 }, { opacity: 1 }], c0, T);
      run(to, fr.in, c0, T);
      run(scene, fr.out, c0, T, sceneOpts(fr.out));
    } else if (fr.inTop) {
      run(to, [{ opacity: 1 }, { opacity: 1 }], c0, T);
      run(to, fr.in, c0, T);
    } else if (fr.outTop) {
      run(to, [{ opacity: 1 }, { opacity: 1 }], c0, T);
      onTop(scene, c0, T);
      run(scene, fr.out, c0, T, sceneOpts(fr.out));
    }

    animsRef.current = A;
    playTimerRef.current = window.setTimeout(() => stop(), total + 400);
  }, [cfg.transitionSec, cfg.holdSec, cfg.transitionType, cfg.motion, cfg.motionAmount, stop]);

  useEffect(() => () => stop(), [stop]);

  useEffect(() => {
    if (playRequest) play();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playRequest]);

  /* ── Keyboard ── */
  useEffect(() => {
    const onKey = (e) => {
      if (!rootRef.current || !rootRef.current.isConnected) return;
      if (isTypingTarget(e.target)) return;
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === 'z') { e.preventDefault(); if (e.shiftKey) history.redo(); else history.undo(); return; }
      if (mod && e.key.toLowerCase() === 'y') { e.preventDefault(); history.redo(); return; }
      if (!selected) return;
      if (mod && e.key.toLowerCase() === 'd') { e.preventDefault(); duplicateLayer(selected.id); return; }
      if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); removeLayer(selected.id); return; }
      if (e.key === 'Escape') { setSelectedId(null); return; }
      const step = e.shiftKey ? 2 : 0.5;
      const delta = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
      if (delta && !selected.locked) {
        e.preventDefault();
        const b = layerBox(selected, exportDims);
        updateLayer(applyLayerBox(selected, { ...b, cx: b.cx + (delta[0] / 100) * exportDims.w, cy: b.cy + (delta[1] / 100) * exportDims.h }, exportDims));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [history, selected, duplicateLayer, removeLayer, updateLayer, exportDims]);

  const topFirst = [...layers].sort((a, b) => (b.zIndex ?? 0) - (a.zIndex ?? 0));
  const meta = getTransitionMeta(cfg.transitionType);

  return (
    <div className="studio studio--banner" ref={rootRef} data-tab={mobileTab}>
      <div className="studio__bar">
        <div className="studio__title">
          <h3>Transition Banner</h3>
          <span>Shown between narration and clips</span>
        </div>
        <div className="studio__bar-group">
          <Toggle label="Use on generate" checked={bannerEnabled} onChange={setBannerEnabled} />
          <button type="button" className="st-icon-btn" onClick={history.undo} disabled={!history.canUndo} title="Undo (Ctrl+Z)" aria-label="Undo"><FaUndo /></button>
          <button type="button" className="st-icon-btn" onClick={history.redo} disabled={!history.canRedo} title="Redo (Ctrl+Shift+Z)" aria-label="Redo"><FaRedo /></button>
        </div>
        <TemplateBar
          type={TEMPLATE_TYPES.TRANSITION_BANNER}
          user={user}
          userToken={userToken}
          noun="banner"
          buildPayload={() => buildBannerPayload(presetId, normalizeBannerConfig(bannerConfig), bannerEnabled)}
          parsePayload={parseBannerPayload}
          onApply={applySaved}
        />
      </div>

      <div className="studio__body">
        <aside className="studio__rail">
          <div className="studio__panel-head">
            <div className="st-seg" style={{ flex: 1 }}>
              <button type="button" className={railTab === 'layers' ? 'is-active' : ''} onClick={() => setRailTab('layers')}>Layers</button>
              <button type="button" className={railTab === 'presets' ? 'is-active' : ''} onClick={() => setRailTab('presets')}>Presets</button>
            </div>
          </div>

          {railTab === 'layers' ? (
            <>
              <div className="st-add-grid">
                {ADD_ITEMS.map((it) => (
                  <button key={it.kind} type="button" className="st-add" onClick={() => addLayer(it.kind)} title={`Add ${it.label.toLowerCase()}`}>
                    {it.icon}<span>{it.label}</span>
                  </button>
                ))}
              </div>
              {topFirst.length === 0 ? (
                <p className="st-empty">No layers yet — add one above or pick a preset.</p>
              ) : (
                <ul className="st-layers">
                  {topFirst.map((layer, idx) => (
                    <li
                      key={layer.id}
                      className={[
                        'st-layer',
                        selectedId === layer.id ? 'is-selected' : '',
                        layer.visible === false ? 'is-hidden' : '',
                        dragLayerId === layer.id ? 'is-dragging' : '',
                        dropLayerId === layer.id && dragLayerId !== layer.id ? 'is-drop-target' : '',
                      ].filter(Boolean).join(' ')}
                      draggable
                      onDragStart={(e) => { setDragLayerId(layer.id); e.dataTransfer.effectAllowed = 'move'; }}
                      onDragOver={(e) => { e.preventDefault(); setDropLayerId(layer.id); }}
                      onDragLeave={() => setDropLayerId((d) => (d === layer.id ? null : d))}
                      onDrop={(e) => { e.preventDefault(); reorder(dragLayerId, layer.id); setDragLayerId(null); setDropLayerId(null); }}
                      onDragEnd={() => { setDragLayerId(null); setDropLayerId(null); }}
                    >
                      <button type="button" className="st-layer__main" onClick={() => { setSelectedId(layer.id); setMobileTab('inspect'); }}>
                        <span className="st-layer__thumb">{layerIcon(layer)}</span>
                        <span className="st-layer__name">
                          <strong>{layerLabel(layer)}</strong>
                          <span>{layer.type === 'image' && isCircleImageLayer(layer) ? 'circle image' : layer.type}{layer.locked ? ' · locked' : ''}</span>
                        </span>
                      </button>
                      <span className="st-layer__actions">
                        <button type="button" className="st-icon-btn st-layer__arrange" onClick={() => moveStep(layer.id, 'up')} disabled={idx === 0} title="Bring forward" aria-label="Bring forward"><FaArrowUp /></button>
                        <button type="button" className="st-icon-btn st-layer__arrange" onClick={() => moveStep(layer.id, 'down')} disabled={idx === topFirst.length - 1} title="Send backward" aria-label="Send backward"><FaArrowDown /></button>
                        <button type="button" className={`st-icon-btn ${layer.locked ? 'is-on' : ''}`} onClick={() => updateLayer({ ...layer, locked: !layer.locked }, { commit: true })} title={layer.locked ? 'Unlock' : 'Lock'} aria-label={layer.locked ? 'Unlock' : 'Lock'}>
                          {layer.locked ? <FaLock /> : <FaLockOpen />}
                        </button>
                        <button type="button" className="st-icon-btn" onClick={() => updateLayer({ ...layer, visible: layer.visible === false }, { commit: true })} title={layer.visible === false ? 'Show' : 'Hide'} aria-label={layer.visible === false ? 'Show' : 'Hide'}>
                          {layer.visible === false ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <div className="st-presets">
              {BANNER_PRESETS.map((p) => (
                <button key={p.id} type="button" className={`st-preset ${presetId === p.id ? 'is-selected' : ''}`} onClick={() => applyPreset(p)}>
                  <span className="st-preset__thumb"><BannerThumb config={p.config} /></span>
                  <span className="st-preset__body"><strong>{p.label}</strong><span>{p.description}</span></span>
                </button>
              ))}
            </div>
          )}
        </aside>

        <section className="studio__stage">
          <div className="st-stage__toolbar">
            <button type="button" className="st-btn st-btn--sm st-btn--primary" onClick={() => (playing ? stop() : play())}>
              <FaPlay /> {playing ? 'Stop' : 'Play'}
            </button>
            <label className="st-transition-pick">
              <span>Transition</span>
              <select
                className="st-select"
                value={cfg.transitionType}
                title={meta?.description}
                onChange={(e) => { setConfig({ transitionType: e.target.value }, { commit: true }); setPlayRequest((n) => n + 1); }}
              >
                {TRANSITION_GROUPS.map((g) => (
                  <optgroup key={g.group} label={g.group}>
                    {g.ids.map((id) => <option key={id} value={id}>{getTransitionMeta(id).label}</option>)}
                  </optgroup>
                ))}
              </select>
            </label>
            <span className="st-stage__meta">{cfg.transitionSec.toFixed(2)}s · hold {cfg.holdSec.toFixed(2)}s</span>
            <span className="st-spacer" />
            <Toggle label="Snap" checked={snapping} onChange={setSnapping} />
            <Toggle label="Safe area" checked={showSafe} onChange={setShowSafe} />
            <span className="st-stage__meta">{exportDims.w}×{exportDims.h}</span>
          </div>
          <BannerCanvas
            config={cfg}
            orientation={orient}
            selectedId={selectedId}
            onSelect={(id) => { setSelectedId(id); if (id) setMobileTab('inspect'); }}
            onLayerChange={(l) => updateLayer(l)}
            onCheckpoint={history.checkpoint}
            showSafe={showSafe}
            snapping={snapping}
            sceneRef={sceneRef}
            demoRef={demoRef}
            playing={playing}
          />
        </section>

        <div className="studio__tabs" role="tablist">
          <button type="button" role="tab" aria-selected={mobileTab === 'rail'} className={mobileTab === 'rail' ? 'is-active' : ''} onClick={() => setMobileTab('rail')}>
            Layers &amp; presets
          </button>
          <button type="button" role="tab" aria-selected={mobileTab === 'inspect'} className={mobileTab === 'inspect' ? 'is-active' : ''} onClick={() => setMobileTab('inspect')}>
            {selected ? 'Edit layer' : 'Banner settings'}
          </button>
        </div>

        <aside className="studio__inspector">
          <div className="studio__panel-head">
            <span>{selected ? layerLabel(selected) : 'Banner settings'}</span>
            {selected && <button type="button" className="st-btn st-btn--sm" onClick={() => setSelectedId(null)}>Done</button>}
          </div>
          {selected ? (
            <LayerInspector
              key={selected.id}
              layer={selected}
              exportDims={exportDims}
              onChange={(l) => updateLayer(l)}
              onDuplicate={() => duplicateLayer(selected.id)}
              onArrange={(dir) => moveStep(selected.id, dir)}
              onDelete={() => removeLayer(selected.id)}
              userToken={userToken}
            />
          ) : (
            <BannerSettings cfg={cfg} setConfig={setConfig} onReplay={() => setPlayRequest((n) => n + 1)} />
          )}
        </aside>
      </div>
    </div>
  );
}

export { DEFAULT_BANNER_CONFIG, FFMPEG_TRANSITIONS };
